import { format } from 'date-fns';
import { Box, ButtonBase, TableCell, TableRow, Typography } from '@mui/material';

const toFixed2 = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '0.00');

const getSurveyAnswer = (user, sortOrder) => {
  if (!user?.latestSurvey) return 'N/A';
  const value = user.latestSurvey?.answersBySortOrder?.[sortOrder];
  return typeof value === 'string' && value.trim() ? value : 'No response';
};

export default function ScorecardUserCard({ user, surveyQuestions, onIssueClick }) {
  const q1 = surveyQuestions?.[0] || null;
  const q2 = surveyQuestions?.[1] || null;
  const totalIssues = user?.quality?.totalIssues ?? 0;
  const productivityPct = user?.productivity?.productivityPercent ?? 0;

  const productivityColor =
    productivityPct >= 85
      ? 'success.main'
      : productivityPct < 80
        ? 'error.main'
        : 'text.primary';

  const cellSx = {
    px: 1,
    py: 1,
    fontSize: '0.8rem',
    verticalAlign: 'top',
    borderBottom: '1px solid',
    borderColor: 'divider',
  };

  return (
    <TableRow
      hover
      sx={{
        '&:last-child td': { borderBottom: 0 },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Name */}
      <TableCell sx={{ ...cellSx, minWidth: 140 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {user.userName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {user.email}
        </Typography>
      </TableCell>

      {/* Task Info */}
      <TableCell sx={{ ...cellSx, minWidth: 110 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {`Total: ${user.taskSummary?.totalTasks ?? 0}\nDone: ${user.taskSummary?.completed ?? 0}\nIn Progress: ${user.taskSummary?.inProgress ?? 0}`}
        </Typography>
      </TableCell>

      {/* Planned hrs */}
      <TableCell sx={{ ...cellSx, minWidth: 80 }}>
        <Typography variant="body2">{toFixed2(user.productivity?.plannedHours)}</Typography>
      </TableCell>

      {/* Productive hrs */}
      <TableCell sx={{ ...cellSx, minWidth: 90 }}>
        <Typography variant="body2">{toFixed2(user.productivity?.productiveHours)}</Typography>
      </TableCell>

      {/* Productivity % */}
      <TableCell sx={{ ...cellSx, minWidth: 90 }}>
        <Typography variant="body2" color={productivityColor} fontWeight={600}>
          {toFixed2(productivityPct)}%
        </Typography>
      </TableCell>

      {/* Issues */}
      <TableCell sx={{ ...cellSx, minWidth: 65 }}>
        {totalIssues > 0 ? (
          <ButtonBase
            onClick={() => onIssueClick?.(user)}
            sx={{
              typography: 'body2',
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              color: 'primary.main',
              borderRadius: 1,
              px: 0.5,
            }}
          >
            {totalIssues}
          </ButtonBase>
        ) : (
          <Typography variant="body2">0</Typography>
        )}
      </TableCell>

      {/* Survey Q1 */}
      <TableCell sx={{ ...cellSx, minWidth: 220 }}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          {q1 ? getSurveyAnswer(user, q1.sortOrder) : '—'}
        </Typography>
      </TableCell>

      {/* Survey Q2 */}
      <TableCell sx={{ ...cellSx, minWidth: 220 }}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          {q2 ? getSurveyAnswer(user, q2.sortOrder) : '—'}
        </Typography>
      </TableCell>

      {/* Submitted At */}
      <TableCell sx={{ ...cellSx, minWidth: 110 }}>
        {user.latestSurvey?.submittedAt ? (
          <Box>
            <Typography variant="body2">
              {format(new Date(user.latestSurvey.submittedAt), 'dd MMM yy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(user.latestSurvey.submittedAt), 'hh:mm a')}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">
            N/A
          </Typography>
        )}
      </TableCell>

      {/* Survey Date */}
      <TableCell sx={{ ...cellSx, minWidth: 105 }}>
        <Typography variant="body2">
          {user.latestSurvey?.surveyDate
            ? format(new Date(user.latestSurvey.surveyDate), 'dd MMM yyyy')
            : 'N/A'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
